import React, { CSSProperties, ReactNode, useState } from 'react';
import { RequestStateEnum, UserRoleEnum, useTakeOwnershipMutation } from '../../../generated/graphql';
import { useCurrentUser, useSuccessAlert } from '../../../App.hooks';
import { newGuid, ticksToDate } from '../../../Utils';
import AssignResourceToRequestDialog from '../../dialogs/AssignResourceToRequestDialog/AssignResourceToRequestDialog';
import DownloadDialog from '../../dialogs/DownloadDialog/DownloadDialog';
import EditRequestDialog from '../../dialogs/governance/EditRequestDialog/EditRequestDialog';
import DeleteRequestDialog from '../../dialogs/governance/DeleteRequestDialog/DeleteRequestDialog';
import FinalReviewRequestDialog from '../../dialogs/FinalReviewRequestDialog/FinalReviewRequestDialog';
import HistoryDialog from '../../dialogs/HistoryDialog/HistoryDialog';
import { formatApplicationName, formatRequestTags } from './DataTableGovernance.utils';
import {
    Check, User, UserCheck, Pencil, Info, Download, Trash2
} from 'lucide-react';
import DataTableNew, { DataTableColumn } from '../DataTableNew/DataTableNew';
import { TableSort } from '../DataTableNew/DataTableSorting';

type ActionProps = {
    row: any,
    onRefetch?: () => void,
};

const ActionBtn = ({ title, icon: Icon, color, onClick }: {
    title: string;
    icon: React.ComponentType<any>;
    color: 'success' | 'error' | 'default';
    onClick: () => void;
}) => {
    const colors = {
        success: { color: 'var(--success, #16a34a)', bg: 'var(--success-soft, #f0fdf4)', hover: '#dcfce7' },
        error: { color: 'var(--error, #dc2626)', bg: 'var(--error-soft, #fef2f2)', hover: '#fee2e2' },
        default: { color: 'var(--text-secondary)', bg: 'transparent', hover: 'var(--bg-hover)' },
    };
    const c = colors[color];
    return (
        <button
            title={title}
            onClick={onClick}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                backgroundColor: c.bg, color: c.color, transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = c.hover; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = c.bg; }}
        >
            <Icon size={14} strokeWidth={2} />
        </button>
    );
};

const ActionButtons = ({ row, onRefetch }: ActionProps) => {
    const [openCloseRequest, setOpenCloseRequest] = useState(false);
    const [openReassignRequest, setOpenReassignRequest] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [openEditRequest, setOpenEditRequest] = useState(false);
    const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
    const [openDeleteRequest, setOpenDeleteRequest] = useState(false);
    const [takeOwnership] = useTakeOwnershipMutation();
    const currentUser = useCurrentUser();
    const setSuccessAlert = useSuccessAlert();
    const isOpen = row?.state === RequestStateEnum.Open;
    const isLibraryAdmin = row?.library?.isAdmin === true;
    const isAssigned = row?.assignedToId === currentUser?.id;

    const onCloseHandler = () => {
        setOpenCloseRequest(false);
        setOpenReassignRequest(false);
        setOpenHistory(false);
        setOpenEditRequest(false);
        setOpenDownloadDialog(false);
        setOpenDeleteRequest(false);
        if (onRefetch) onRefetch();
    };

    const handleTakeOwnership = () => {
        if (!row?.id) return;
        takeOwnership({ variables: { id: row.id, message: 'Ownership taken from the request queue.' } })
            .then(() => { setSuccessAlert('Request ownership updated!'); onRefetch?.(); });
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isOpen && (isAssigned || isLibraryAdmin) &&
                    <ActionBtn title="Edit request" icon={Pencil} color="default" onClick={() => setOpenEditRequest(true)} />}
                {isOpen && isLibraryAdmin && !isAssigned &&
                    <ActionBtn title="Take ownership" icon={UserCheck} color="default" onClick={handleTakeOwnership} />}
                {isOpen && isLibraryAdmin &&
                    <ActionBtn title="Reassign" icon={User} color="default" onClick={() => setOpenReassignRequest(true)} />}
                {isOpen && isLibraryAdmin &&
                    <ActionBtn title="Close request" icon={Check} color="success" onClick={() => setOpenCloseRequest(true)} />}
                <ActionBtn title="History / Info" icon={Info} color="default" onClick={() => setOpenHistory(true)} />
                {row?.attachment &&
                    <ActionBtn title="Download" icon={Download} color="default" onClick={() => setOpenDownloadDialog(true)} />
                }
                {currentUser?.role === UserRoleEnum.Admin &&
                    <ActionBtn title="Delete request" icon={Trash2} color="error" onClick={() => setOpenDeleteRequest(true)} />}
            </div>
            {openDownloadDialog && <DownloadDialog key={newGuid()} visible={openDownloadDialog} request={row} onClose={onCloseHandler} />}
            {openCloseRequest && <FinalReviewRequestDialog key={newGuid()} visible={openCloseRequest} request={row} onClose={onCloseHandler} />}
            {openReassignRequest && <AssignResourceToRequestDialog key={newGuid()} visible={openReassignRequest} request={row} onClose={onCloseHandler} />}
            {openHistory && <HistoryDialog key={newGuid()} visible={openHistory} request={row} onClose={onCloseHandler} />}
            {openEditRequest && <EditRequestDialog key={row?.id ?? 'edit-request'} visible={openEditRequest} request={row} onClose={onCloseHandler} />}
            {openDeleteRequest && <DeleteRequestDialog key={newGuid()} visible={openDeleteRequest} request={row} onClose={onCloseHandler} />}
        </>
    );
};

type DataTableGovernanceProps = {
    isLoading: boolean,
    onSearchBoxChanged?: (value: string) => void,
    onSelected?: (ids: string[]) => void,
    onRefetch?: () => void,
    fetchMore?: () => void,
    hasMore?: boolean,
    customProps?: { id: string, name: string; }[],
    link?: string,
    data: any[] | undefined | null,
    sort: TableSort | null,
    onSortChange: (sort: TableSort | null) => void,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const COLUMNS: DataTableColumn[] = [
    { id: 'client', name: 'Client', render: (row) => row.client?.name ?? '' },
    { id: 'library', name: 'Library', render: (row) => row.library?.name ?? '' },
    { id: 'applicationName', name: 'Application', render: (row) => formatApplicationName(row.applicationName) },
    { id: 'tags', name: 'Tags', render: (row) => formatRequestTags(row.tags) },
    { id: 'state', name: 'State', width: '100px', sortField: 'state', render: (row) => {
        const state = row.state as string | undefined;
        if (!state) return null;
        const isOpen = state === RequestStateEnum.Open;
        return <span className={isOpen ? 'badge-warning' : 'badge-default'}>{isOpen ? 'Open' : 'Closed'}</span>;
    }},
    { id: 'assignedTo', name: 'Assigned To', width: '140px', render: (row) => row.assignedTo?.name ?? '' },
    { id: 'createdBy', name: 'Created By', width: '140px', render: (row) => row.createdBy?.name ?? '' },
    { id: 'updatedTime', name: 'Updated', width: '130px', sortField: 'updatedTime', render: (row) => <span>{row.updatedTime ? ticksToDate(row.updatedTime) : ''}</span> },
];

const DataTableGovernance = (props: DataTableGovernanceProps) => {
    const { style, isLoading, data, onSearchBoxChanged, fetchMore, onRefetch, sort, onSortChange } = props;
    const columns: DataTableColumn[] = [...COLUMNS, {
        id: 'actions',
        name: 'Actions',
        width: '180px',
        render: (row) => <ActionButtons row={row} onRefetch={onRefetch} />,
    }];

    return (
        <DataTableNew
            style={style}
            isLoading={isLoading}
            data={data}
            columns={columns}
            searchable={!!onSearchBoxChanged}
            onSearchChanged={onSearchBoxChanged}
            selectable={false}
            fetchMore={fetchMore}
            sortingMode="server"
            sort={sort}
            onSortChange={onSortChange}
            emptyTitle="No requests found"
        />
    );
};

export default DataTableGovernance;
